import {Injectable} from '@angular/core';
import {map, Observable, switchMap} from 'rxjs';
import {NativeServiceApiProvider} from '../../../../services/native-api-provider.service';
import {OwnershipLocalState} from '../../../model/book-model';
import {BookListEntry, V3DrmProduct, V3ProductShare} from '../../../model/v3/drm';
import {BooksNativeServiceApiV3} from '../books-native-api-v3';

/**
 * `BooksNativeServiceApiV3` implementation for the non-web platforms. The books data
 * on mobile intentionally stays on the unchanged native bridge (see the DRM v3 migration
 * plan - the JS<->native contract does not change in any stage), so this class acts as
 * a bridge: it delegates to the platform's legacy `BooksNativeServiceApi` implementation
 * (resolved by `NativeServiceApiProvider.books()`) and maps the legacy types to the
 * v3 view model. Fields absent in the legacy model (`share.id`, `labels`, publication
 * data) stay null - the views do not use them.
 */
@Injectable({
    providedIn: 'root'
})
export class LegacyBooksBridgeV3 implements BooksNativeServiceApiV3 {

    constructor(private provider: NativeServiceApiProvider) {}

    getProducts(): Observable<BookListEntry[]> {
        return this.provider.books().pipe(
            switchMap(api => api.getProducts()),
            map(ownershipsLS => ownershipsLS.map(ownershipLS => this.toEntry(ownershipLS)))
        )
    }

    getProductById(productId: number): Observable<BookListEntry> {
        return this.provider.books().pipe(
            switchMap(api => api.getProductById(productId)),
            map(ownershipLS => this.toEntry(ownershipLS))
        )
    }

    getImageUrl(productId: number, releaseId?: number): Observable<string> {
        return this.provider.books().pipe(
            switchMap(api => api.getImageUrl(productId, releaseId))
        )
    }

    private toEntry(localState: OwnershipLocalState): BookListEntry {
        const eBook = localState.ownership.eBook
        const product: V3DrmProduct = {
            id: eBook.id,
            name: eBook.name,
            description: eBook.description,
            order: eBook.order,
            categories: eBook.categories,
            path: eBook.path,
            labels: null,
            publicationDate: null,
            displayPublic: null,
        }
        const share: V3ProductShare = {
            id: null,
            product: product,
            emptySlots: localState.ownership.availableShares,
        }
        return {
            share: share,
            releaseId: eBook.release?.id,
            downloaded: localState.downloaded != null,
            hasProduct: !!localState.ownership.hasProduct,
            usedDevices: localState.ownership.devicesShared?.length ?? 0,
        }
    }
}
